<template>
  <div class="card shadow-sm mt-3">
    <div class="card-body">
      <div>
        <Multiselect
          v-model="selectedCampaign"
          :options="campaigns"
          :multiple="false"
          :taggable="false"
          track-by="campaignId"
          label="name"
          placeholder="Select a campaign"
          :loading="isLoadingCampaigns"
        />
      </div>

      <div class="align-items-center mt-3">
        <div class="row d-flex align-items-center">
          <div class="col-md-6 d-flex justify-content-center custom-control custom-radio">
            <input
              id="campaign-report"
              v-model="selectedReportType"
              type="radio"
              class="custom-control-input"
              value="campaign"
            >
            <label
              class="custom-control-label"
              for="campaign-report"
            >
              Campaign Report
            </label>
          </div>
          <div class="col-md-6 d-flex justify-content-center custom-control custom-radio">
            <input
              id="creative-report"
              v-model="selectedReportType"
              type="radio"
              class="custom-control-input"
              value="creative"
            >
            <label
              class="custom-control-label"
              for="creative-report"
            >
              Creative Report
            </label>
          </div>
        </div>
        <div class="row d-flex align-items-center mt-3">
          <div class="col-md-6">
            <label
              for="start-date"
              class="mb-0 mr-2 font-weight-bold"
            >
              Start Date
            </label>
            <input
              id="start-date"
              v-model="startDate"
              type="date"
              class="form-control form-control-sm mr-3"
              style="min-width: 140px;"
              :max="yesterdayDate"
            >
          </div>
          <div class="col-md-6">
            <label
              for="end-date"
              class="mb-0 mr-2 font-weight-bold"
            >
              End Date
            </label>
            <input
              id="end-date"
              v-model="endDate"
              type="date"
              class="form-control form-control-sm"
              style="min-width: 140px;"
              :max="yesterdayDate"
            >
          </div>
        </div>
      </div>

      <div
        v-if="error"
        class="alert alert-danger d-flex justify-content-center w-100 mt-3"
        role="alert"
      >
        <i class="fas fa-exclamation-triangle mr-2 mt-1" />{{ error }}
      </div>

      <div class="mt-3 d-flex justify-content-end">
        <button
          :disabled="isLoading || isLoadingCampaigns"
          class="btn btn-primary btn-sm"
          @click="getReport"
        >
          <i
            v-if="!isLoading"
            class="fas fa-file-download mr-2"
          />
          <i
            v-if="isLoading"
            class="fas fa-spinner fa-spin mr-2"
          />
          Get report
        </button>
      </div>

      <div
        v-if="csvData"
        class="table-responsive mt-4"
      >
        <table class="table table-bordered table-hover table-sm bg-white">
          <thead class="thead-light">
            <tr>
              <th
                v-for="(header, idx) in csvData[0]"
                :key="'header-' + idx"
              >
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIdx) in csvData.slice(1)"
              :key="'row-' + rowIdx"
            >
              <td
                v-for="(cell, cellIdx) in row"
                :key="'cell-' + rowIdx + '-' + cellIdx"
              >
                {{ cell }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="mt-3 d-flex justify-content-end">
          <button
            class="btn btn-outline-primary btn-sm mt-2"
            @click="downloadCSV"
          >
            <i class="fas fa-download mr-1" />
            Download CSV
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
    WALMART_EVENTS,
    walmartEventBroker
} from '@frontend/group/modules/walmart/event-broker';
import Multiselect from 'vue-multiselect';

export default {
    name: 'WalmartReports',

    components: { Multiselect },

    data: () => ({
        platform: null,
        selectedReportType: 'campaign',
        startDate: '2023-05-25',
        endDate: '2023-06-16',
        csvAvailable: false,
        csvData: null,
        csvPath: null,
        isLoading: false,
        error: null,
        campaigns: [],
        selectedCampaign: null,
        isLoadingCampaigns: false,
    }),

    computed: {
        yesterdayDate() {
            return this.getDateNDaysAgo(1);
        },
        dayBeforeYesterdayDate() {
            return this.getDateNDaysAgo(2);
        }
    },

    mounted() {
        walmartEventBroker.on(WALMART_EVENTS.REPORT_INIT, async () => {
            this.platform = $('#deployment_platform').find(':selected').val();

            await this.getCampaigns();

            this.startDate = this.dayBeforeYesterdayDate;
            this.endDate = this.yesterdayDate;
        });
    },

    methods: {
        async getReport() {
            this.error = null;
            this.isLoading = true;
            this.csvAvailable = false;
            this.csvPath = null;
            this.csvData = null;

            try {
                const { data } = await axios.post('/walmart-report/handle-report', {
                    report_type: this.selectedReportType,
                    start_date: this.startDate,
                    end_date: this.endDate,
                    platform: this.platform,
                    campaign_id: this.selectedCampaign ? this.selectedCampaign.campaignId : null,
                });

                if (data.status === 'error') {
                    this.error = data.message || 'An error occurred while generating the report.';
                    return;
                }

                this.csvAvailable = true;
                this.csvPath = data.path;
                this.csvData = data.csvData || null;
                return;
            } catch (error) {
                this.error = error?.response?.data?.message || error.message || 'An unexpected error occurred.';
            } finally {
                this.isLoading = false;
            }
        },

        downloadCSV() {
            window.location.href = `/walmart-report/download-csv?file_path=${this.csvPath}`;

            this.csvAvailable = false;
            this.csvPath = null;
        },

        getDateNDaysAgo(n) {
            const d = new Date();
            d.setDate(d.getDate() - n);
            return d.toISOString().slice(0, 10);
        },

        async getCampaigns() {
            this.error = null;
            this.campaigns = [];
            this.isLoadingCampaigns = true;

            try {
                const { data } = await axios.post('/walmart/list-all-campaigns', {
                    platform: this.platform,
                });

                if (data.status === 'error') {
                    this.error = data.message || 'An error occurred while fetching campaigns.';
                    return;
                }

                this.campaigns = data.data.response;
            } catch (error) {
                this.error = error?.response?.data?.message || error.message || 'An unexpected error occurred.';
            } finally {
                this.isLoadingCampaigns = false;
            }
        }
    }
};
</script>

<style scoped>
.table-responsive {
    max-height: 400px;
    overflow-y: auto;
}

.table-sm th,
.table-sm td {
    padding: 0.15rem 0.3rem !important;
    font-size: 0.78rem !important;
    vertical-align: middle;
}

.table-sm thead th {
    font-size: 0.82rem !important;
    font-weight: 600;
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
}

.table-sm {
    font-size: 0.78rem;
}
</style>
